import { useState, useMemo, useRef } from 'react';
import { DocumentIcon, FolderIcon } from '@heroicons/react/24/outline';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter } from '../FormComponent';
import { flattenFolders, getDisplayFileName } from './helpers';
import styles from '../../styles/assetsManagement/filesManager.module.scss';

const MoveFileModal = ({ file, currentFolderId, rawFolders, onConfirm, onClose, loading }) => {
  const pickable = useMemo(() => {
    const all = flattenFolders(rawFolders || []);
    return all.filter((f) => f.id !== currentFolderId);
  }, [rawFolders, currentFolderId]);

  const [selectedFolderId, setSelectedFolderId] = useState(
    pickable.length > 0 ? pickable[0].id : null
  );
  const itemRefs = useRef([]);

  // Keep selection valid when pickable changes
  const resolvedSelection =
    selectedFolderId && pickable.some((f) => f.id === selectedFolderId)
      ? selectedFolderId
      : pickable.length > 0
        ? pickable[0].id
        : null;

  const handleKeyDown = (e, idx) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      itemRefs.current[Math.min(idx + 1, pickable.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      itemRefs.current[Math.max(idx - 1, 0)]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (pickable[idx]) setSelectedFolderId(pickable[idx].id);
    }
  };

  const buttons = [
    { label: 'Cancel', variant: 'outline', className: 'primary', onClick: onClose, disabled: loading },
    {
      label: loading ? 'Moving…' : 'Move Here',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: () => onConfirm(resolvedSelection),
      disabled: loading || !resolvedSelection,
    },
  ];

  return (
    <ModalWrapper
      isOpen
      onClose={onClose}
      title="Move File"
      size="small"
      footer={<CommonFooter buttons={buttons} />}
    >
      <div className={styles.moveModalBody}>
        <div className={styles.movingFromRow}>
          <span className={styles.movingLabel}>Moving:</span>
          <div className={styles.movingFolder}>
            <DocumentIcon className={styles.movingFolderIcon} />
            <span className={styles.movingFolderName} title={getDisplayFileName(file)}>
              {getDisplayFileName(file) || '—'}
            </span>
          </div>
        </div>

        <p className={styles.movePickerLabel}>Select destination folder:</p>

        <div
          className={styles.folderPickerList}
          role="radiogroup"
          aria-label="Select destination folder"
        >
          {pickable.map((f, i) => {
            const isSelected = resolvedSelection === f.id;
            return (
              <div
                key={f.id}
                ref={(el) => { itemRefs.current[i] = el; }}
                className={[
                  styles.folderPickerItem,
                  isSelected ? styles.folderPickerSelected : '',
                ].join(' ')}
                style={{ paddingLeft: `${(f.level || 0) * 20 + 12}px` }}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => setSelectedFolderId(f.id)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                title={f.folderPath}
              >
                <FolderIcon className={styles.pickerIcon} />
                <span className={styles.pickerItemName}>{f.folderName}</span>
                {f.level === 0 && <span className={styles.pickerLevelBadge}>L0</span>}
                {f.level === 1 && <span className={styles.pickerLevelBadge}>L1</span>}
                {isSelected && <span className={styles.pickerCheck} aria-hidden="true">✓</span>}
              </div>
            );
          })}

          {pickable.length === 0 && (
            <p className={styles.pickerEmpty}>No other folders available to move this file to.</p>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default MoveFileModal;
